/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useRef, useState } from 'react';
import type { ContentChangeListener } from '@volkswagen-onehub/audi-content-service';

import type { TrackingServiceV2 } from './feature-service-types';
import type { AppContent, FeatureAppProps } from '../../@types/interfaces';

import { EditorialText } from './components/EditorialText';
import { normalizeContent } from './components/normalizeContent';

import mockContent from './content.json';
import { getNextId } from '../utils/getNextId';
import { apolloClient } from './constants';
import { ApolloProvider } from '@apollo/client';

import { detectPlacement } from '../utils/detectFaPlacement';
import type { PlacementResult } from '../utils/detectFaPlacement';

const FeatureApp: React.FC<FeatureAppProps> = ({ ...inherited }: FeatureAppProps) => {
  const {
    contentService,
    trackingService: trackingServiceInherited,
    footnoteReferenceService,
    vueFormatterService,
    localeService,
    logger,
    config,
  } = inherited;

  let trackingService = {} as TrackingServiceV2;

  if (typeof window !== 'undefined') {
    trackingService = trackingServiceInherited!;
  }

  const hasValidConfig = config && ('fields' in config || 'theme' in config);
  const [rawContent, setRawContent] = useState(
    hasValidConfig ? config : (contentService?.getContent() as AppContent),
  );
  contentService?.onContentChange(setRawContent as ContentChangeListener);

  useEffect(() => {
    const featureAppId = trackingService?.featureAppId ?? getNextId(__FEATURE_APP_NAME__);

    trackingService.featureAppName = 'editorial-text';
    const additionalObjects = {
      componentInfo: {
        componentID: featureAppId,
      },
      attributes: {
        implementer: 2,
        version: __FEATURE_APP_VERSION__,
      },
    };

    const dataLayerEvent = {
      eventInfo: {
        eventAction: 'feature_app_ready',
        eventName: 'editorial text - feature app ready',
      },
      attributes: {
        componentName: 'editorial-text',
        label: '',
        targetURL: '',
        clickID: '',
        elementName: '',
        value: '',
        pos: '',
      },
    };
    trackingService.track({ event: dataLayerEvent, componentUpdate: additionalObjects });
  }, []);

  useEffect(() => {
    const listener = () => {
      setRawContent(normalizeContent(contentService?.getContent() as AppContent));
    };

    contentService?.onContentChange(listener);

    return () => {
      contentService?.removeOnContentChange(listener);
    };
  }, [contentService, setRawContent]);

  const content = normalizeContent(rawContent ?? mockContent);

  // -------------------------------------------------------------------------
  //                            SITEWIDE SEARCH
  // -------------------------------------------------------------------------

  const [placement, setPlacement] = useState<PlacementResult | null>(null);
  const layoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const result = detectPlacement(layoutRef.current);
      setPlacement(result);
    } catch (error) {
      setPlacement(null);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      logger?.error(
        'Failed to detect Feature App placement.',
        { event: 'placement-detection-failed' },
        error,
      );
    }
  }, [logger]);

  const isAcceptableMarket = localeService?.market === 'USA' || localeService?.market === 'CA';
  const showSearch = isAcceptableMarket && content.enableSearch;

  console.log(placement);

  // -------------------------------------------------------------------------

  return (
    <ApolloProvider client={apolloClient}>
      <EditorialText
        content={content}
        footnoteReferenceService={footnoteReferenceService}
        vueFormatterService={vueFormatterService}
        localeService={localeService}
        trackingService={trackingService}
        showSearch={showSearch}
      />
    </ApolloProvider>
  );
};

export default FeatureApp;
